import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { isNativeApp } from '@/lib/nativeApp';
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useHostBranding } from "@/components/HostBranding";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { SupportPanel } from "@/components/SupportPanel";
import { useTheme } from "next-themes";
import { trackEvent, ConversionEvents } from "@/lib/analytics";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { DOCS_CATEGORIES, DOCS_ARTICLES } from "@/data/docsArticles";
import '@/pages/landing-animations.css';

// Top-of-funnel CTA — the global "Start for free" / "Sign up" button.
// Without this event we can't tell which surface (header, blog, pricing)
// brings visitors to /auth.
function trackHeaderCta(label: string, target: string) {
  trackEvent(ConversionEvents.CTA_CLICKED, {
    source: 'header',
    label: label.slice(0, 64),
    href: target.slice(0, 200),
    path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

export const MarketingHeader = ({ dashboardMode, onOpenSettings, onOpenSupport, onLogout }: { dashboardMode?: boolean; onOpenSettings?: () => void; onOpenSupport?: () => void; onLogout?: () => void } = {}) => {
  const { user, signOut } = useAuth();
  const brand = useHostBranding(); // non-null on a customer's branded host → swap our logo for theirs
  // A branded host and the store app serve only the app: no marketing menus.
  const appOnly = !!brand || isNativeApp();
  const { t } = useTranslation(['translation', 'common']);
  const { theme, resolvedTheme, setTheme } = useTheme();
  const lp = useLocalizedPath();

  const toggleTheme = () => {
    const isDark = resolvedTheme === 'dark';
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('cctvplanner_theme_chosen', next);
  };
  const [mounted, setMounted] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Which mega menu is open (Product / Calculators). Hover intent: trigger
  // and panel are DOM siblings, so a shared 140ms grace timer keeps the
  // panel open while the pointer crosses the gap between them.
  const [openMenu, setOpenMenu] = useState<null | 'product' | 'calculators' | 'docs'>(null);
  const megaCloseTimer = useRef<number | null>(null);
  const openMega = (menu: 'product' | 'calculators' | 'docs') => {
    if (megaCloseTimer.current) { clearTimeout(megaCloseTimer.current); megaCloseTimer.current = null; }
    setOpenMenu(menu);
  };
  const keepMegaOpen = () => {
    if (megaCloseTimer.current) { clearTimeout(megaCloseTimer.current); megaCloseTimer.current = null; }
  };
  const scheduleMegaClose = () => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
    megaCloseTimer.current = window.setTimeout(() => setOpenMenu(null), 140);
  };
  const closeMega = () => setOpenMenu(null);

  useEffect(() => { setMounted(true); }, []);

  // Switching language navigates between structurally different route
  // entries (unprefixed "/" vs "/:lang/..."), which unmounts and remounts
  // this header. If the page is already scrolled when that remount
  // happens, some browsers insert the new `position: sticky` element
  // without recomputing its stuck state, so it renders at its unstuck
  // (off-screen) position until the next scroll or forced layout pass —
  // it looks like the header vanished. Nudging the scroll position by a
  // sub-pixel amount on mount forces that recompute immediately, with no
  // visible movement.
  useLayoutEffect(() => {
    const y = window.scrollY;
    if (y > 0) {
      window.scrollTo(0, y - 1);
      window.scrollTo(0, y);
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const productLinks = [
    // Not in the top bar: at 1280px the bar already clips the CTA in pl/de/fr, one more item clips it in en too.
    { to: lp("/free"), label: t('free_plan_nav', { defaultValue: 'Free plan' }) },
    { to: lp("/features"), label: t('features') },
    { to: lp("/how-it-works"), label: t('how_it_works') },
    { to: lp("/camera-physics"), label: t('camera_physics_nav', { defaultValue: 'Camera physics' }) },
    { to: lp("/use-cases"), label: t('use_cases') },
  ];

  const navLinks = [
    { to: lp("/pricing"), label: t('pricing') },
    { to: lp("/blog"), label: t('blog') },
  ];

  // Docs mega menu content comes straight from the docs registry, so the
  // panel stays in sync when articles are added. Titles live in `common`,
  // which this component already loads.
  const docsByCategory = DOCS_CATEGORIES.map((cat) => ({
    cat,
    articles: DOCS_ARTICLES
      .filter((a) => a.category === cat.key)
      .sort((a, b) => a.order - b.order),
  }));
  const docArticlePath = (a: (typeof DOCS_ARTICLES)[number]) =>
    a.externalPath ? lp(`/${a.externalPath}`) : lp(`/docs/${a.slug}`);

  return (
    <>
      <header className="pelco-header">
        <div className="pelco-container h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo — brand refresh V4 horizontal lockup (mark + wordmark
                with .io accent). Light/dark variants swapped via CSS visibility. */}
            <Link to={lp("/")} className="flex-shrink-0 flex items-center" aria-label={`${brand?.name || 'CCTVplanner.io'} home`}>
              {brand ? (
                brand.logoUrl
                  ? <img src={brand.logoUrl} alt={brand.name} className="h-9 lg:h-12 w-auto block object-contain" />
                  : <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.01em', color: 'var(--color-gray-dark)' }}>{brand.name}</span>
              ) : (
                <>
                  <img src="/logos/logo-horizontal-dark.svg" alt="CCTVplanner.io" className="h-9 lg:h-12 w-auto block dark:hidden" />
                  <img src="/logos/logo-horizontal-light.svg" alt="CCTVplanner.io" className="h-9 lg:h-12 w-auto hidden dark:block" />
                </>
              )}
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-7">
              {/* Mega-menu triggers — read as tabs while open. A branded host
                  keeps only the calculators (the rest is our marketing, which
                  BrandedHostGuard does not serve there) and its own way home. */}
              {!appOnly && (
              <div onMouseEnter={() => openMega('product')} onMouseLeave={scheduleMegaClose}>
                <button
                  className="lv7-nav-link lv7-nav-tab flex items-center gap-1"
                  data-open={openMenu === 'product' || undefined}
                  aria-expanded={openMenu === 'product'}
                  aria-haspopup="true"
                  onClick={() => setOpenMenu(openMenu === 'product' ? null : 'product')}
                >
                  {t('footer_product', 'Product')}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'product' ? 'rotate-180' : ''}`} />
                </button>
              </div>
              )}
              {/* Calculators is a real link (the hub page matters for SEO);
                  hovering it opens its own mega menu. */}
              <div onMouseEnter={() => openMega('calculators')} onMouseLeave={scheduleMegaClose}>
                <Link
                  to={lp("/calculators")}
                  className="lv7-nav-link lv7-nav-tab flex items-center gap-1.5"
                  data-open={openMenu === 'calculators' || undefined}
                  aria-expanded={openMenu === 'calculators'}
                  aria-haspopup="true"
                  onClick={closeMega}
                >
                  {t('calculators_nav')}
                  {!appOnly && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(91, 140, 255, 0.15)', color: 'var(--color-brand-blue-text)' }}>
                      {t('nav_badge_free', { defaultValue: 'Free' })}
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'calculators' ? 'rotate-180' : ''}`} />
                </Link>
              </div>

              {brand?.homeUrl && (
                <a href={brand.homeUrl} className="lv7-nav-link">
                  {t('nav_back_to_zone', { defaultValue: 'Back to the designer zone' })}
                </a>
              )}

              {!appOnly && navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="lv7-nav-link"
                >
                  {link.label}
                </Link>
              ))}
              {!appOnly && (
              <div onMouseEnter={() => openMega('docs')} onMouseLeave={scheduleMegaClose}>
                <Link
                  to={lp("/docs")}
                  className="lv7-nav-link lv7-nav-tab flex items-center gap-1"
                  data-open={openMenu === 'docs' || undefined}
                  aria-expanded={openMenu === 'docs'}
                  aria-haspopup="true"
                  onClick={closeMega}
                >
                  {t('docs_nav')}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'docs' ? 'rotate-180' : ''}`} />
                </Link>
              </div>
              )}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center gap-2">
                <LanguageSelector />
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-gray-muted)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-hover-overlay)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {mounted ? (resolvedTheme === 'dark' ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />) : <span className="w-4.5 h-4.5 inline-block" />}
                </button>
              </div>

              {mounted && user ? (
                <div className="hidden lg:flex items-center gap-3">
                  {dashboardMode ? (
                    <>
                      {onOpenSupport && (
                        <button onClick={onOpenSupport} className="lv7-nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                          {t('dashboard:support_nav', { defaultValue: 'Support' })}
                        </button>
                      )}
                      {/* The dashboard supplies onLogout so the page can leave the
                          protected route afterwards; a bare signOut stays on it. */}
                      <button onClick={() => (onLogout ?? signOut)()} className="lv7-nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        {t('common:logout', { defaultValue: 'Log out' })}
                      </button>
                      {onOpenSettings && (
                        <button onClick={onOpenSettings} className="lv7-pill lv7-pill--sm lv7-pill--accent">
                          {t('dashboard:settings_btn', { defaultValue: 'Settings' })}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard" className="lv7-nav-link">
                        {t('common:dashboard')}
                      </Link>
                      <Link to="/cctv-design" className="lv7-pill lv7-pill--sm lv7-pill--accent">
                        {t('hero2_cta_primary', { defaultValue: 'Open the designer' })}
                      </Link>
                    </>
                  )}
                </div>
              ) : (
                <div className="hidden lg:flex items-center gap-3">
                  <button type="button" onClick={() => setContactOpen(true)} className="lv7-nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                    {t('contact_nav', { defaultValue: 'Contact' })}
                  </button>
                  <Link to="/auth" className="lv7-nav-link">
                    {t('common:login')}
                  </Link>
                  {!appOnly && (
                  <Link to="/auth?mode=signup&redirect=/cctv-design" className="lv7-pill lv7-pill--sm lv7-pill--accent"
                    onClick={() => trackHeaderCta(t('plan_cta_get_started', { defaultValue: 'Start for free' }), '/auth?mode=signup')}>
                    {t('plan_cta_get_started', { defaultValue: 'Start for free' })}
                  </Link>
                  )}
                </div>
              )}

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 active:scale-95 active:opacity-70"
                style={{ color: 'var(--color-gray-dark)' }}
                aria-label={t('common:open_menu')}
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Aurora-style mega menu: full-width sheet under the bar with link
            columns and thumbnail cards. Desktop only (mobile has the drawer);
            every label reuses an already-translated key. */}
        {openMenu === 'product' && (
          <div
            className="lv7-mega hidden lg:block"
            onMouseEnter={keepMegaOpen}
            onMouseLeave={scheduleMegaClose}
          >
            <div className="pelco-container grid grid-cols-[1fr_1fr_1.3fr] gap-12 py-10">
              <div>
                <p className="lv7-mega-heading">{t('footer_product', 'Product')}</p>
                {productLinks.map((link) => (
                  <Link key={link.to} to={link.to} onClick={closeMega} className="lv7-mega-link">
                    {link.label}
                  </Link>
                ))}
              </div>
              <div>
                <p className="lv7-mega-heading">{t('footer_tools', 'Tools')}</p>
                <Link to={lp("/calculators")} onClick={closeMega} className="lv7-mega-link">
                  {t('calculators_nav')}
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(91, 140, 255, 0.15)', color: 'var(--color-brand-blue-text)' }}>
                    {t('nav_badge_free', { defaultValue: 'Free' })}
                  </span>
                </Link>
                <Link to={lp("/cameras")} onClick={closeMega} className="lv7-mega-link">
                  {t('footer_camera_catalog', { defaultValue: 'Camera Catalog' })}
                </Link>
                <Link to={lp("/glossary")} onClick={closeMega} className="lv7-mega-link">
                  {t('footer_glossary', { defaultValue: 'Glossary' })}
                </Link>
              </div>
              <div className="border-s ps-10" style={{ borderColor: 'var(--color-gray-border)' }}>
                <p className="lv7-mega-heading">{t('footer_resources', 'Resources')}</p>
                <Link to={lp("/demo")} onClick={closeMega} className="lv7-mega-card">
                  <img src="/media/bystra/hero-s1.webp" alt="" width={168} height={112} loading="lazy" className="lv7-mega-thumb" />
                  <span className="text-[15px] font-medium" style={{ color: 'var(--color-gray-dark)', fontFamily: 'var(--font-ui), sans-serif' }}>
                    {t('hero2_cta_secondary', { defaultValue: 'View a sample project' })}
                  </span>
                </Link>
                <Link to={lp("/blog")} onClick={closeMega} className="lv7-mega-card">
                  <img src="/media/bystra/3d.webp" alt="" width={168} height={112} loading="lazy" className="lv7-mega-thumb" />
                  <span className="text-[15px] font-medium" style={{ color: 'var(--color-gray-dark)', fontFamily: 'var(--font-ui), sans-serif' }}>
                    {t('blog')}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        )}

        {openMenu === 'calculators' && (
          <div
            className="lv7-mega hidden lg:block"
            onMouseEnter={keepMegaOpen}
            onMouseLeave={scheduleMegaClose}
          >
            <div className="pelco-container grid grid-cols-[1fr_1fr_1.3fr] gap-12 py-10">
              <div>
                <p className="lv7-mega-heading">{t('footer_calculators', 'Calculators')}</p>
                {[
                  { to: "/calculator/fov", label: t('footer_fov_calculator', 'FOV Calculator') },
                  { to: "/calculator/dori", label: t('footer_dori_calculator', 'DORI Calculator') },
                  { to: "/calculator/lens-selector", label: t('footer_lens_calculator', 'Lens Calculator') },
                  { to: "/calculator/storage", label: t('footer_storage_calculator', 'Storage Calculator') },
                  { to: "/calculator/bandwidth", label: t('footer_bandwidth_calculator_landing', 'Bandwidth Calculator') },
                  { to: "/calculator/poe", label: t('footer_poe_calculator', 'PoE Budget Calculator') },
                ].map((link) => (
                  <Link key={link.to} to={lp(link.to)} onClick={closeMega} className="lv7-mega-link">
                    {link.label}
                  </Link>
                ))}
              </div>
              <div>
                <p className="lv7-mega-heading">{t('lp7_nav_standards', { defaultValue: 'Standards and guides' })}</p>
                {[
                  { to: "/en-62676-4-calculator", label: t('footer_en_62676', 'EN 62676-4 Standard') },
                  { to: "/johnson-criteria-calculator", label: t('footer_johnson', 'Johnson Criteria') },
                  // Marketing pages: a branded host does not serve them.
                  ...(appOnly ? [] : [
                    { to: "/camera-physics", label: t('footer_camera_physics', 'Camera Physics') },
                    { to: "/nvr", label: t('footer_nvr_catalog', 'NVR & Recorder Catalog') },
                  ]),
                ].map((link) => (
                  <Link key={link.to} to={lp(link.to)} onClick={closeMega} className="lv7-mega-link">
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="border-s ps-10" style={{ borderColor: 'var(--color-gray-border)' }}>
                <p className="lv7-mega-heading">{t('footer_resources', 'Resources')}</p>
                <Link to={lp("/calculators")} onClick={closeMega} className="lv7-mega-card">
                  <img src="/feature-pdf.webp" alt="" width={168} height={112} loading="lazy" className="lv7-mega-thumb" />
                  <span className="text-[15px] font-medium" style={{ color: 'var(--color-gray-dark)', fontFamily: 'var(--font-ui), sans-serif' }}>
                    {t('calc_link_all', { defaultValue: 'Open the standalone calculators' })}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        )}

        {openMenu === 'docs' && (
          <div
            className="lv7-mega hidden lg:block"
            onMouseEnter={keepMegaOpen}
            onMouseLeave={scheduleMegaClose}
          >
            <div className="pelco-container grid grid-cols-[1fr_1fr_1.3fr] gap-12 py-10">
              <div>
                <p className="lv7-mega-heading">{t(`common:${docsByCategory[0].cat.titleKey}`)}</p>
                {docsByCategory[0].articles.map((a) => (
                  <Link key={a.slug} to={docArticlePath(a)} onClick={closeMega} className="lv7-mega-link">
                    {t(`common:${a.titleKey}`)}
                  </Link>
                ))}
              </div>
              <div>
                {docsByCategory.slice(1).map(({ cat, articles }) => (
                  <div key={cat.key} className="mb-6 last:mb-0">
                    <p className="lv7-mega-heading">{t(`common:${cat.titleKey}`)}</p>
                    {articles.map((a) => (
                      <Link key={a.slug} to={docArticlePath(a)} onClick={closeMega} className="lv7-mega-link">
                        {t(`common:${a.titleKey}`)}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
              <div className="border-s ps-10" style={{ borderColor: 'var(--color-gray-border)' }}>
                <p className="lv7-mega-heading">{t('footer_resources', 'Resources')}</p>
                <Link to={lp("/docs")} onClick={closeMega} className="lv7-mega-card">
                  <img src="/media/bystra/locate.webp" alt="" width={168} height={112} loading="lazy" className="lv7-mega-thumb" />
                  <span className="text-[15px] font-medium" style={{ color: 'var(--color-gray-dark)', fontFamily: 'var(--font-ui), sans-serif' }}>
                    {t('footer_docs', 'Documentation')}
                  </span>
                </Link>
                <div className="mt-3">
                  <Link to={lp("/faq")} onClick={closeMega} className="lv7-mega-link">
                    {t('footer_faq', 'FAQ')}
                  </Link>
                  <Link to={lp("/use-cases")} onClick={closeMega} className="lv7-mega-link">
                    {t('footer_use_cases', 'Use Cases')}
                  </Link>
                  <Link to={lp("/changelog")} onClick={closeMega} className="lv7-mega-link">
                    {t('footer_changelog', 'Changelog')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 end-0 bottom-0 w-[300px] max-w-[85vw] flex flex-col animate-in slide-in-from-right rtl:slide-in-from-left" style={{ backgroundColor: 'var(--color-page-bg)' }}>
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 border-b" style={{ height: 'var(--header-height)', borderColor: 'var(--color-gray-border)', paddingTop: 'env(safe-area-inset-top)' }}>
              <div className="flex items-center gap-2">
                {brand ? (
                  <>
                    {brand.logoUrl && <img src={brand.logoUrl} alt="" className="h-7 w-auto object-contain" />}
                    <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em', color: 'var(--color-gray-dark)' }}>
                      {brand.name}
                    </span>
                  </>
                ) : (
                  <>
                    {/* Mark-only reads cleanly on the drawer's page-bg; variants swap by theme. */}
                    <img src="/logos/logo-mark.svg" alt="" className="w-7 h-7 block dark:hidden" />
                    <img src="/logos/logo-mark-on-dark.svg" alt="" className="w-7 h-7 hidden dark:block" />
                    <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em', color: 'var(--color-gray-dark)' }}>
                      CCTVplanner
                    </span>
                  </>
                )}
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2 -me-2" style={{ color: 'var(--color-gray-muted)' }} aria-label={t('common:close_menu')}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer links */}
            <nav className="flex-1 overflow-y-auto px-5 py-6 space-y-1" style={{ fontFamily: 'var(--font-ui), sans-serif' }}>
              {brand && (
                <>
                  <Link to={lp("/calculators")} onClick={() => setMobileOpen(false)}
                    className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                    {t('calculators_nav')}
                  </Link>
                  {brand.homeUrl && (
                    <a href={brand.homeUrl} className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                      {t('nav_back_to_zone', { defaultValue: 'Back to the designer zone' })}
                    </a>
                  )}
                </>
              )}
              {!brand && productLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                  {link.label}
                </Link>
              ))}
              {!brand && <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />}
              {!appOnly && navLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                  {link.label}
                </Link>
              ))}

              {mounted && user && (
                <>
                  <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}
                    className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                    {t('common:dashboard')}
                  </Link>
                  {dashboardMode && onOpenSupport && (
                    <button
                      onClick={() => { onOpenSupport(); setMobileOpen(false); }}
                      className="block py-3 text-base font-medium w-full text-start"
                      style={{ color: 'var(--color-gray-dark)' }}
                    >
                      {t('dashboard:support_nav', { defaultValue: 'Support' })}
                    </button>
                  )}
                  {dashboardMode && onOpenSettings && (
                    <button
                      onClick={() => { onOpenSettings(); setMobileOpen(false); }}
                      className="block py-3 text-base font-medium w-full text-start"
                      style={{ color: 'var(--color-gray-dark)' }}
                    >
                      {t('dashboard:settings_btn', { defaultValue: 'Settings' })}
                    </button>
                  )}
                  <button onClick={() => { (onLogout ?? signOut)(); setMobileOpen(false); }}
                    className="block py-3 text-base font-medium text-red-500 w-full text-start">
                    {t('common:logout', { defaultValue: 'Log out' })}
                  </button>
                </>
              )}

              {mounted && !user && (
                <>
                  <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
                  <button type="button"
                    onClick={() => { setContactOpen(true); setMobileOpen(false); }}
                    className="block py-3 text-base font-medium w-full text-start" style={{ color: 'var(--color-gray-dark)' }}>
                    {t('contact_nav', { defaultValue: 'Contact' })}
                  </button>
                  <Link to="/auth" onClick={() => setMobileOpen(false)}
                    className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-muted)' }}>
                    {t('common:login')}
                  </Link>
                  <Link to="/auth?mode=signup&redirect=/cctv-design"
                    onClick={() => {
                      trackHeaderCta(t('plan_cta_get_started', { defaultValue: 'Start for free' }), '/auth?mode=signup');
                      setMobileOpen(false);
                    }}
                    className="block mt-3 py-3 text-center text-base font-bold rounded-full bg-[var(--lv7-accent)] text-[var(--color-on-brand,#101318)]">
                    {t('plan_cta_get_started', { defaultValue: 'Start for free' })}
                  </Link>
                </>
              )}

              <div className="pt-4 flex items-center gap-3">
                <LanguageSelector />
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-gray-muted)', border: '1px solid var(--color-gray-border)' }}
                  aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {mounted ? (resolvedTheme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />) : <span className="w-5 h-5 inline-block" />}
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}

      <SupportPanel
        variant="contact"
        open={contactOpen}
        onOpenChange={setContactOpen}
        userEmail={user?.email}
        isPremium={false}
      />
    </>
  );
};
